import * as React from 'react'
import { cn } from '../../lib/utils'
import type { ButtonProps } from './button'

/**
 * ButtonGroup — joins related `Button`s into a single connected unit for
 * *actions*: a toolbar, a stepper (−/value/+), or a split button. From the
 * TailGrids button-group model wired to our `Button`; it collapses the inner
 * corners and overlaps the shared 1px border so the row reads as one control.
 *
 * For single-select of a value (Day/Week/Month, list/map) use `SegmentedControl`
 * (a styled radio group); for switching content panels use `Tabs`. ButtonGroup
 * is not a selection control — it has no "selected" state.
 *
 *   <ButtonGroup size="md">
 *     <Button variant="primary" appearance="outline">Day</Button>
 *     <Button variant="primary" appearance="outline">Week</Button>
 *     <Button variant="primary" appearance="outline">Month</Button>
 *   </ButtonGroup>
 *
 * `size` is set on the group and flows to every child Button (the full Button
 * scale: `xs | sm | md | lg | xl`); a child's own `size` still wins if set.
 * There's no group-level color — each Button keeps its own `variant`/
 * `appearance`, so you can mix (e.g. a filled primary action beside outline
 * buttons, or highlight the active segment with a fill). The focused/hovered
 * button is raised so its ring and border paint above its neighbours.
 */

type ButtonGroupSize = NonNullable<ButtonProps['size']>
type Orientation = 'horizontal' | 'vertical'

export interface ButtonGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Size applied to every child Button (matches the Button scale). */
  size?: ButtonGroupSize
  /** Lay the buttons in a row (default) or a column. */
  orientation?: Orientation
}

/** Per-position corner-collapse + border-overlap classes for a joined button. */
const joinClass = (
  index: number,
  count: number,
  orientation: Orientation
): string => {
  if (count <= 1) return ''
  const isFirst = index === 0
  const isLast = index === count - 1
  return cn(
    'relative hover:z-10 focus-visible:z-10',
    orientation === 'horizontal'
      ? [!isFirst && '-ml-px rounded-l-none', !isLast && 'rounded-r-none']
      : [!isFirst && '-mt-px rounded-t-none', !isLast && 'rounded-b-none']
  )
}

export const ButtonGroup = React.forwardRef<HTMLDivElement, ButtonGroupProps>(
  (
    { className, size, orientation = 'horizontal', children, ...props },
    ref
  ) => {
    const items = React.Children.toArray(children).filter(
      React.isValidElement
    ) as React.ReactElement<ButtonProps>[]
    const count = items.length
    return (
      <div
        ref={ref}
        role="group"
        className={cn(
          'isolate inline-flex',
          orientation === 'vertical' && 'flex-col',
          className
        )}
        {...props}
      >
        {items.map((child, index) =>
          React.cloneElement(child, {
            size: child.props.size ?? size,
            className: cn(
              joinClass(index, count, orientation),
              child.props.className
            ),
          })
        )}
      </div>
    )
  }
)
ButtonGroup.displayName = 'ButtonGroup'
